import { useState, useEffect } from 'react';
import { getWordPressPosts } from '../../services/wordpress';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { SEO } from '../../components/seo/SEO';
import { NewsSkeleton } from '../../components/skeletons/NewsSkeleton';
import { AdSense } from '../../components/ads/AdSense';
import { PageLayout } from '../../components/layout/PageLayout';
import { supabase } from '../../lib/supabase';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from '../../hooks/useTranslation';
import { useI18n } from '../../contexts/I18nContext';

export function News() {
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page')) || 1;
  const { t, lang } = useTranslation();
  const { getPathForLang } = useI18n();

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      const { posts, totalPages } = await getWordPressPosts(currentPage);
      setPosts(posts);
      setTotalPages(totalPages);
      setLoading(false);
      window.scrollTo(0, 0);
    };

    fetchPosts();
  }, [currentPage]);

  const handlePageChange = (newPage: number) => {
    setSearchParams({ page: newPage.toString() });
  };

  // Fonction pour nettoyer le HTML
  const cleanHtml = (html: string) => {
    return html.replace(/<[^>]*>/g, '');
  };

  if (loading) {
    return <NewsSkeleton />;
  }

  return (
    <>
      <SEO 
        title={t('news.seo.title')}
        description={t('news.seo.description')}
        og_image="https://blog.pokemonpocket.fr/wp-content/uploads/2023/11/blog-banner.jpg"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Section d'en-tête */}
          <div className="px-4 sm:px-6 lg:px-8 pt-12 pb-16 border-b border-neutral-200 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-primary-50 text-primary-700 mb-6">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                </svg>
                {t('news.latest_news')}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-neutral-900 mb-8 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-400">
                {t('news.latest_news_heading')}
              </h1>
              <div className="max-w-3xl mx-auto">
                <p className="text-lg md:text-xl text-neutral-600 leading-relaxed">
                  {t('news.description')}
                </p>
              </div>
            </div>
          </div>

          {/* Annonce AdSense unique */}
          <div className="mt-6 mb-8 flex justify-center items-center">
            <AdSense adSlot="1424794127" id="news-main" />
          </div>

          {/* Section des articles */}
          <div className="px-4 sm:px-6 lg:px-8 py-12">
            <h2 className="text-2xl font-bold text-neutral-900 mb-8">{t('news.latest_articles')}</h2>
            
            {posts.length > 0 ? (
              <>
                {/* Article principal */}
                {posts[0] && (
                  <article className="mb-16">
                    <div className="grid lg:grid-cols-2 gap-8 bg-gradient-to-b from-neutral-50 to-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-neutral-100">
                      {posts[0].featured_media_url && (
                        <div className="aspect-[16/9] lg:aspect-[4/3] overflow-hidden bg-neutral-100">
                          <img
                            src={posts[0].featured_media_url}
                            alt=""
                            className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      )}
                      <div className="flex flex-col justify-center p-8">
                        <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 mb-4">
                          {t('news.featured_article')}
                        </div>
                        <h3
                          className="text-2xl lg:text-3xl font-bold text-neutral-900 mb-4 line-clamp-2"
                          title={cleanHtml(posts[0].title.rendered)}
                          dangerouslySetInnerHTML={{ __html: posts[0].title.rendered }}
                        />
                        <div
                          className="text-neutral-600 prose prose-lg line-clamp-3 mb-6"
                          dangerouslySetInnerHTML={{ __html: posts[0].excerpt.rendered }}
                        />
                        <Link
                          to={getPathForLang(`/actualites/${posts[0].slug}`)}
                          className="inline-flex items-center text-primary hover:text-primary-dark font-semibold group text-lg"
                        >
                          {t('news.read_full_article')}
                          <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </article>
                )}

                {/* Grille d'articles */}
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {posts.slice(1).map((post) => (
                    <article
                      key={post.id}
                      className="group flex flex-col bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-neutral-100"
                    >
                      {post.featured_media_url && (
                        <div className="aspect-[16/9] overflow-hidden bg-neutral-100">
                          <img
                            src={post.featured_media_url}
                            alt=""
                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      )}
                      <div className="flex flex-col flex-1 p-6">
                        <div className="mb-4">
                          <time className="text-sm text-neutral-500">
                            {new Date(post.date).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </time>
                        </div>
                        <h3
                          className="text-xl font-bold text-neutral-900 group-hover:text-primary transition-colors line-clamp-2 mb-3"
                          title={cleanHtml(post.title.rendered)}
                          dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                        />
                        <div
                          className="text-neutral-600 prose line-clamp-3 mb-4 flex-grow"
                          dangerouslySetInnerHTML={{ __html: post.excerpt.rendered }}
                        />
                        <Link
                          to={getPathForLang(`/actualites/${post.slug}`)}
                          className="inline-flex items-center text-primary hover:text-primary-dark font-semibold group mt-auto"
                        >
                          {t('news.read_more')}
                          <ChevronRight className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-neutral-100">
                <div className="w-16 h-16 mx-auto mb-4 text-neutral-400">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2 2 0 00-2-2h-2" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-neutral-900 mb-2">{t('news.no_articles.title')}</h3>
                <p className="text-neutral-600">
                  {t('news.no_articles.description')}
                </p>
              </div>
            )}
          </div>

          {/* Section des fonctionnalités */}
          <div className="px-4 sm:px-6 lg:px-8 py-12 bg-gradient-to-br from-neutral-50 to-white border-t border-neutral-200">
            <h2 className="text-2xl font-bold text-neutral-900 mb-8">{t('news.discover_title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white/90 p-6 rounded-lg shadow-sm border border-neutral-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-neutral-900">{t('news.format.title')}</h3>
                </div>
                <p className="text-sm text-neutral-600">
                  {t('news.format.description')}
                </p>
              </div>

              <div className="bg-white/90 p-6 rounded-lg shadow-sm border border-neutral-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-neutral-900">{t('news.card_types.title')}</h3>
                </div>
                <p className="text-sm text-neutral-600">
                  {t('news.card_types.description')}
                </p>
              </div>

              <div className="bg-white/90 p-6 rounded-lg shadow-sm border border-neutral-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-neutral-900">{t('news.events.title')}</h3>
                </div>
                <p className="text-sm text-neutral-600">
                  {t('news.events.description')}
                </p>
              </div>

              <div className="bg-white/90 p-6 rounded-lg shadow-sm border border-neutral-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-neutral-900">{t('news.guides.title')}</h3>
                </div>
                <p className="text-sm text-neutral-600">
                  {t('news.guides.description')}
                </p>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-br from-neutral-50 to-white border-t border-neutral-200">
            <h2 className="text-2xl font-bold text-neutral-900 mb-6">{t('news.faq.title')}</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-neutral-100">
                <h3 className="font-semibold text-neutral-900 mb-2">{t('news.faq.how_to_start.question')}</h3>
                <p className="text-sm text-neutral-600">
                  {t('news.faq.how_to_start.answer')}
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-neutral-100">
                <h3 className="font-semibold text-neutral-900 mb-2">{t('news.faq.stay_informed.question')}</h3>
                <p className="text-sm text-neutral-600">
                  {t('news.faq.stay_informed.answer')}
                </p>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 sm:px-6 lg:px-8 py-8 border-t border-neutral-200 bg-gradient-to-br from-primary/5 to-primary/10">
              <div className="flex justify-center items-center gap-3">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-100 border border-neutral-200"
                  aria-label={t('news.pagination.previous')}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  const isCurrentPage = pageNumber === currentPage;
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`w-11 h-11 rounded-lg text-sm font-semibold transition-colors border
                        ${isCurrentPage 
                          ? 'bg-primary text-white border-primary' 
                          : 'text-neutral-700 hover:bg-neutral-100 border-neutral-200'
                        }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-100 border border-neutral-200"
                  aria-label={t('news.pagination.next')}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
